/**
 * Phase 2 MM13 — Supplier / RFQ tab (project studio).
 * Generate RFQ from project data → readiness/missing → fit shortlist →
 * quote intake + tiers → comparison matrix → recommendation DRAFT →
 * human decision. Honesty: not a supplier approval / contract / payment.
 */

import { useCallback, useEffect, useState } from "react";
import {
  addQuoteTier, buildComparison, createQuote, createRfq, draftRecommendation,
  exportRfqPack, generateShortlist, getRfqQuotes, getRfqSuppliers, getRfqs,
  logSupplierDecision,
} from "../api/client";
import { Button } from "../components/Button";
import { StatusPill } from "../components/StatusPill";
import type { StudioStrings } from "../i18n/studioStrings";

type Row = Record<string, unknown>;

export function SupplierRfqTab({ projectId, t }:
  { projectId: string; t: StudioStrings }): JSX.Element {
  const [rfqs, setRfqs] = useState<Row[]>([]);
  const [active, setActive] = useState<Row | null>(null);
  const [section, setSection] = useState("shortlist");
  const [shortlist, setShortlist] = useState<Row[]>([]);
  const [quotes, setQuotes] = useState<Row[]>([]);
  const [comparison, setComparison] = useState<Row | null>(null);
  const [decisionId, setDecisionId] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [title, setTitle] = useState("");
  const [quoteForm, setQuoteForm] = useState({ supplier_id: "", currency: "USD",
    unit_price: "", lead_time_days: "" });

  const refresh = useCallback(async () => {
    const r = await getRfqs(projectId);
    if (r.outcome === "ok") setRfqs(r.data?.items ?? []);
    else setNotice(r.status === 403 ? t.permissionDenied : t.loadError);
  }, [projectId, t.permissionDenied, t.loadError]);

  const loadSection = useCallback(async (rfq: Row, sec: string) => {
    setActive(rfq); setSection(sec);
    const rid = String(rfq.id);
    if (sec === "shortlist") {
      const r = await getRfqSuppliers(projectId, rid);
      setShortlist(r.outcome === "ok" ? r.data?.items ?? [] : []);
    } else if (sec === "quotes") {
      const r = await getRfqQuotes(projectId, rid);
      setQuotes(r.outcome === "ok" ? r.data?.items ?? [] : []);
    }
  }, [projectId]);

  useEffect(() => { void refresh(); }, [refresh]);

  async function onGenerate(): Promise<void> {
    if (title.trim().length < 2) { setNotice(t.loadError); return; }
    const r = await createRfq(projectId, { title });
    setNotice(r.outcome === "ok" ? null
      : r.status === 403 ? t.permissionDenied : r.errorMessage);
    if (r.outcome === "ok") setTitle("");
    await refresh();
  }

  async function onShortlist(): Promise<void> {
    if (!active) return;
    await generateShortlist(projectId, String(active.id));
    await loadSection(active, "shortlist");
  }

  async function onCompare(): Promise<void> {
    if (!active) return;
    const r = await buildComparison(projectId, String(active.id));
    if (r.outcome === "ok") {
      setComparison(r.data?.matrix as Row ?? null);
      setSection("comparison");
      setActive({ ...active, _cmpId: (r.data?.comparison as Row)?.id });
    } else setNotice(r.errorMessage);
  }

  async function onAddQuote(): Promise<void> {
    if (!active || quoteForm.supplier_id.trim().length < 8) { setNotice(t.loadError); return; }
    const rid = String(active.id);
    const q = await createQuote(projectId, rid, {
      supplier_id: quoteForm.supplier_id, currency: quoteForm.currency,
      lead_time_days: quoteForm.lead_time_days ? Number(quoteForm.lead_time_days) : null });
    if (q.outcome === "ok" && quoteForm.unit_price) {
      await addQuoteTier(projectId, rid, String((q.data?.item as Row)?.id),
        { quantity: 5000, unit_price: Number(quoteForm.unit_price) });
    }
    setNotice(q.outcome === "ok" ? null
      : q.status === 403 ? t.permissionDenied : q.errorMessage);
    await loadSection(active, "quotes");
  }

  async function onRecommend(): Promise<void> {
    if (!active || !active._cmpId) return;
    const r = await draftRecommendation(projectId, String(active.id),
      String(active._cmpId));
    if (r.outcome === "ok") {
      setDecisionId(String((r.data?.decision as Row)?.id));
      setNotice(`${t.recommendation}: ${(r.data?.recommendation as Row)?.recommendation_type}`);
    } else setNotice(r.errorMessage);
  }

  async function onLogDecision(decision: string): Promise<void> {
    if (!active || !decisionId) return;
    const note = decision === "accepted_for_negotiation" ? "proceed"
      : window.prompt(t.notesLabel) ?? "";
    const r = await logSupplierDecision(projectId, String(active.id),
      decisionId, decision, note);
    setNotice(r.outcome === "ok" ? `${t.humanDecision}: ${decision}` : r.errorMessage);
  }

  return (
    <section data-testid="project-suppliers">
      <p className="po-disclaimer" data-testid="supplier-honesty">
        {t.notSupplierApproval}
      </p>
      {notice ? <p role="alert" className="po-error">{notice}</p> : null}

      <div className="po-row" data-testid="rfq-create">
        <input placeholder={t.nameLabel} value={title} data-testid="rfq-title"
               onChange={(e) => setTitle(e.target.value)} />
        <Button variant="primary" onClick={() => void onGenerate()}>{t.newRfq}</Button>
      </div>

      {rfqs.length === 0 ? <p data-testid="rfq-empty">{t.emptyList}</p> : (
        <table className="po-table" data-testid="rfq-list">
          <thead><tr><th>{t.nameLabel}</th><th>{t.statusLabel}</th>
            <th>{t.readiness}</th></tr></thead>
          <tbody>
            {rfqs.map((r) => (
              <tr key={String(r.id)} style={{ cursor: "pointer" }}
                  onClick={() => void loadSection(r, "shortlist")}>
                <td>{String(r.title)}</td>
                <td><StatusPill tone="info" label={String(r.status)} /></td>
                <td>{String(r.readiness_score)}%</td>
              </tr>))}
          </tbody>
        </table>)}

      {active ? (
        <div data-testid="rfq-detail">
          <h2>{String(active.title)} — {String(active.readiness_score)}%</h2>
          {Array.isArray(active.missing_information) && active.missing_information.length ? (
            <p data-testid="rfq-missing">{t.missingFields}:{" "}
              {(active.missing_information as string[]).slice(0, 4).join(" · ")}</p>) : null}
          <nav className="po-tabs">
            {[["shortlist", t.shortlist], ["quotes", t.quotesSection],
              ["comparison", t.comparisonSection]].map(([k, label]) => (
              <button key={k} type="button" aria-pressed={section === k}
                      className={section === k ? "po-tab po-tab--active" : "po-tab"}
                      onClick={() => void loadSection(active, k)}>{label}</button>))}
          </nav>

          {section === "shortlist" ? (
            <div data-testid="rfq-shortlist">
              <Button variant="secondary" onClick={() => void onShortlist()}>
                {t.shortlist}
              </Button>
              <ul>
                {shortlist.map((s) => (
                  <li key={String(s.id)}>
                    <StatusPill tone="ok" label={`${t.fitScore} ${String(s.fit_score)}`} />{" "}
                    {String(s.fit_notes || "—")} · {String(s.manual_send_status)}
                  </li>))}
                {shortlist.length === 0 ? <li>{t.emptyList}</li> : null}
              </ul>
            </div>) : null}

          {section === "quotes" ? (
            <div data-testid="rfq-quotes">
              <div className="po-row" data-testid="quote-create">
                <input placeholder="supplier_id" value={quoteForm.supplier_id}
                       onChange={(e) => setQuoteForm({ ...quoteForm, supplier_id: e.target.value })} />
                <input placeholder="currency" value={quoteForm.currency}
                       onChange={(e) => setQuoteForm({ ...quoteForm, currency: e.target.value })} />
                <input placeholder="unit price" value={quoteForm.unit_price}
                       onChange={(e) => setQuoteForm({ ...quoteForm, unit_price: e.target.value })} />
                <input placeholder="lead days" value={quoteForm.lead_time_days}
                       onChange={(e) => setQuoteForm({ ...quoteForm, lead_time_days: e.target.value })} />
                <Button variant="secondary" onClick={() => void onAddQuote()}>+ {t.quotesSection}</Button>
              </div>
              <ul>
                {quotes.map((q) => (
                  <li key={String(q.id)}>
                    {String(q.supplier_id).slice(0, 8)} · {String(q.currency)} ·{" "}
                    <StatusPill tone="info" label={String(q.status)} /> ·{" "}
                    lead {String(q.lead_time_days ?? "—")}d
                  </li>))}
                {quotes.length === 0 ? <li>{t.emptyList}</li> : null}
              </ul>
              <Button variant="secondary" onClick={() => void onCompare()}>
                {t.comparisonSection}
              </Button>
            </div>) : null}

          {section === "comparison" ? (
            <div data-testid="rfq-comparison">
              {comparison ? (
                <>
                  <p>{t.comparisonSection}: <strong>{String(comparison.status)}</strong>{" "}
                    (confidence {String(comparison.confidence)})</p>
                  <table className="po-table">
                    <thead><tr><th>{t.nameLabel}</th><th>overall</th>
                      <th>unit</th><th>lead</th></tr></thead>
                    <tbody>
                      {(comparison.rows as Row[] ?? []).map((row) => (
                        <tr key={String(row.quote_id)}>
                          <td>{String(row.supplier_name)}</td>
                          <td>{String(row.overall)}</td>
                          <td>{String(row.amortized_unit_base ?? "—")}</td>
                          <td>{String(row.lead_time_days ?? "—")}</td>
                        </tr>))}
                    </tbody>
                  </table>
                  <Button variant="primary" onClick={() => void onRecommend()}>
                    {t.recommendation}
                  </Button>
                  {decisionId ? (
                    <span data-testid="decision-actions">
                      {" "}<Button variant="secondary"
                        onClick={() => void onLogDecision("accepted_for_negotiation")}>
                        {t.humanDecision}: accept
                      </Button>{" "}
                      <Button variant="secondary"
                        onClick={() => void onLogDecision("rejected")}>reject</Button>
                    </span>) : null}
                  <p className="po-disclaimer">{t.notSupplierApproval}</p>
                </>
              ) : <p>{t.emptyList}</p>}
            </div>) : null}

          <p><Button variant="secondary" onClick={() =>
            void exportRfqPack(projectId, String(active.id)).then((r) =>
              setNotice(r.outcome === "ok" ? "RFQ pack exported" : r.errorMessage))}>
            {t.exportPack}
          </Button></p>
        </div>) : null}
    </section>
  );
}
